import { getTourScrollOffset } from './styles';

const VIEWPORT_EDGE_PADDING = 24;
/** Room for a top-placed tooltip between the navbar and the target. */
const TOP_TOOLTIP_ROOM = 340;
/** Estimated tooltip height when placement is below the target. */
const BOTTOM_TOOLTIP_ROOM = 220;

const waitForFrames = (count = 2) =>
  new Promise<void>(resolve => {
    const tick = (left: number) => {
      if (left <= 0) {
        resolve();
        return;
      }
      window.requestAnimationFrame(() => tick(left - 1));
    };
    tick(count);
  });

type ScrollTourTargetOptions = {
  /** Joyride placement — `top` needs space above the target for the tooltip. */
  placement?: string;
  /** Override default room reserved above a top-placed target. */
  topScrollRoom?: number;
};

/**
 * Scroll a tour target into view so the spotlight and tooltip can attach to it.
 * Tall targets (e.g. form cards) align to the top of the viewport instead of
 * centering, which would push the card header off-screen.
 */
export const scrollTourTargetIntoView = async (
  targetSelector: string,
  options: ScrollTourTargetOptions = {},
): Promise<void> => {
  const el = document.querySelector(targetSelector);
  if (!(el instanceof HTMLElement)) {
    return;
  }

  let rect = el.getBoundingClientRect();

  if (
    rect.left < VIEWPORT_EDGE_PADDING ||
    rect.right > window.innerWidth - VIEWPORT_EDGE_PADDING
  ) {
    el.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'center',
    });
    await waitForFrames(2);
    rect = el.getBoundingClientRect();
  }

  const scrollOffset = getTourScrollOffset();
  const visibleTop = scrollOffset + 16;
  const visibleBottom = window.innerHeight - VIEWPORT_EDGE_PADDING;
  const visibleHeight = visibleBottom - visibleTop;
  const placement = options.placement ?? '';
  const isTopPlacement = placement.startsWith('top');
  const isBottomPlacement = placement.startsWith('bottom');
  const topScrollRoom = options.topScrollRoom ?? TOP_TOOLTIP_ROOM;
  const fitsInViewport = rect.height <= visibleHeight;
  const fitsWithBottomTooltip =
    rect.bottom + BOTTOM_TOOLTIP_ROOM <= visibleBottom;
  const isFullyVisible =
    rect.top >= visibleTop &&
    rect.bottom <= visibleBottom &&
    rect.left >= VIEWPORT_EDGE_PADDING &&
    rect.right <= window.innerWidth - VIEWPORT_EDGE_PADDING;

  if (
    isFullyVisible &&
    !(isTopPlacement && rect.top < visibleTop + topScrollRoom) &&
    !(isBottomPlacement && !fitsWithBottomTooltip)
  ) {
    return;
  }

  const absoluteTop = window.scrollY + rect.top;
  let nextTop: number;

  if (isTopPlacement || !fitsInViewport) {
    const topOffset = isTopPlacement
      ? scrollOffset + topScrollRoom
      : visibleTop;
    nextTop = Math.max(0, absoluteTop - topOffset);
  } else if (isBottomPlacement && !fitsWithBottomTooltip) {
    // Show both pool cards from the top; scroll only enough for the tooltip below.
    const absoluteBottom = absoluteTop + rect.height;
    nextTop = Math.max(0, absoluteBottom + BOTTOM_TOOLTIP_ROOM - visibleBottom);
  } else {
    // Short targets: align top to viewport — don't center tall cards on mobile.
    nextTop = Math.max(0, absoluteTop - visibleTop);
  }

  window.scrollTo({ top: nextTop, behavior: 'auto' });
  // Extra frames so Joyride measures after scroll has painted.
  await waitForFrames(4);
};
